<script setup>
import { formatScientific } from '@/utils/formatters'
import { mapUnitOrOriginal } from '@/components/combined-predict/components/CombinedPredictTable/utils/unitConversions'
import { decryptEndpointAbbreviation } from '@/components/combined-predict/models/webtest/webtestColumnsDef'
import ChemPropsWithCopyCard from '@/components/chemical/ChemPropsWithCopyCard.vue'

const popoverFields = [
  { key: 'experimental', label: 'Experimental Value' },
  { key: 'consensus' },
  { key: 'hc', label: 'Hierarchical clustering' },
  { key: 'sm', label: 'Single model' },
  { key: 'gc', label: 'Group contribution' },
  { key: 'nn', label: 'Nearest neighbor' },
]

const getValue = (context, endpointResult, isLogVal) => {
  if (endpointResult?.endpoint?.binary) {
    if (isLogVal) return null
    if (typeof context.active === 'boolean') return decryptEndpointAbbreviation(context.active ? 'A' : 'N')
    return null
  }

  return isLogVal
    ? formatScientific(context.logValue, { unit: endpointResult.endpoint?.logUnits, blank: null })
    : formatScientific(context.value, { unit: endpointResult.endpoint?.units, blank: null })
}

const formatMessage = (method) => {
  return method.message || method.error || method?.resultMessage?.message
}

const findContextByFieldKey = (endpointResult, fieldKey) => {
  if (fieldKey === 'experimental') return endpointResult?.experimental?.[0] ?? null
  return endpointResult?.predicted?.find((p) => p.method === fieldKey) ?? null
}

const getValueByFieldKey = (endpointResult, fieldKey) => {
  const context = findContextByFieldKey(endpointResult, fieldKey)
  if (!context) return null
  return getValue(context, endpointResult, false)
}

const getMessageByFieldKey = (endpointResult, fieldKey) => {
  const context = findContextByFieldKey(endpointResult, fieldKey)
  if (!context) return null
  return formatMessage(context)
}

const getLogValueByFieldKey = (endpointResult, fieldKey) => {
  const context = findContextByFieldKey(endpointResult, fieldKey)
  if (!context) return null
  return getValue(context, endpointResult, true)
}

const getErrorClassByPredictionResult = (prediction) => {
  if (prediction.resultMessage?.severity) {
    if (prediction.resultMessage.severity.toLowerCase() === 'error') return 'text-danger'
    if (prediction.resultMessage.severity.toLowerCase() === 'warning') return 'text-warning'
  }
  return ''
}
</script>

<template>
  <v-card
    v-if="params?.data?.chemical && params?.prediction?.endpoint"
    max-width="30rem"
    max-height="36rem"
    min-width="20rem"
    class="overflow-auto"
  >
    <ChemPropsWithCopyCard
      :chemical="params?.data?.chemical"
      :secondary-title="params?.prediction?.endpoint?.name"
      :props-to-show="['casrn', 'inchiKey']"
      key-value-stacked
      show-in-row
      truncate-props
    />

    <v-divider class="mx-4 my-0" />

    <v-card-text>
      <b-table-lite
        :fields="popoverFields"
        :items="[params?.prediction]"
        borderless
        small
        stacked
        class="mb-0"
      >
        <template #cell()="data">
          <div
            v-if="data.item?.endpoint && findContextByFieldKey(data?.item, data?.field.key)"
            :class="{
              'font-italic': data?.field?.key !== 'experimental',
              'text-underlined': data?.field?.key === 'experimental' && data?.item?.experimental?.length,
              [getErrorClassByPredictionResult(findContextByFieldKey(data?.item, data?.field.key))]: true,
            }"
          >
            <div v-if="getValueByFieldKey(data.item, data.field.key)">
              <span>{{ getValueByFieldKey(data.item, data.field.key) }}</span>
              <span
                v-if="data.item.endpoint?.units"
                class="text-muted small"
              >
                {{ mapUnitOrOriginal(data.item.endpoint?.units) }}
              </span>
            </div>

            <div v-if="getLogValueByFieldKey(data.item, data.field.key)">
              <span>{{ getLogValueByFieldKey(data.item, data.field.key) }}</span>
              <span
                v-if="data.item.endpoint?.logUnits"
                class="text-muted small"
              >
                {{ mapUnitOrOriginal(data.item.endpoint?.logUnits) }}
              </span>
            </div>

            <div v-if="getMessageByFieldKey(data.item, data.field.key)">
              {{ getMessageByFieldKey(data.item, data.field.key) }}
            </div>
          </div>

          <span
            v-else
            class="text-muted"
          >
            –
          </span>
        </template>
      </b-table-lite>
    </v-card-text>
  </v-card>

  <v-card
    v-else
    class="p-2"
  >
    {{ params?.column?.colDef?.headerTooltip }}
  </v-card>
</template>

<style scoped>
.text-underlined {
  text-decoration: underline;
}
</style>
